import { alpha } from '@material-ui/core';

// ----------------------------------------------------------------------

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// SETUP COLORS — Brand palette
// Brand: #ffa305 amber · #514538 dark brown · #b8a99a taupe · #00dabb teal · #00a086 deep teal

// Warm brown-tinted grey scale anchored to brand taupe (#b8a99a) and dark brown (#514538)
// 100/300 pinned to the UI kit's exact `--card` (#f6f0e7) and `--line` (#eae2d6) tokens.
const GREY = {
  0: '#FFFFFF',
  100: '#F6F0E7',
  200: '#F0EDE9',
  300: '#EAE2D6',
  400: '#C4BAB1',
  500: '#B8A99A', // brand taupe — secondary text
  600: '#8A7A6E',
  700: '#6B5C50',
  800: '#514538', // brand dark brown — primary text
  900: '#2E2520',
  500_8: alpha('#B8A99A', 0.08),
  500_12: alpha('#B8A99A', 0.12),
  500_16: alpha('#B8A99A', 0.16),
  500_24: alpha('#B8A99A', 0.24),
  500_32: alpha('#B8A99A', 0.32),
  500_48: alpha('#B8A99A', 0.48),
  500_56: alpha('#B8A99A', 0.56),
  500_80: alpha('#B8A99A', 0.8)
};

// Brand amber — primary CTA
const PRIMARY = {
  lighter: '#FFF3D6',
  light: '#FFD166',
  main: '#FFA305',
  dark: '#D88700', // exact brand "pressed" amber from the UI kit swatch
  darker: '#7A4E00',
  contrastText: '#514538' // brand dark brown — 4.6:1 contrast on amber
};
// Brand teal — secondary / accent
const SECONDARY = {
  lighter: '#CCFAF5',
  light: '#66EDDA',
  main: '#00DABB',
  dark: '#00A086', // exact brand deep teal
  darker: '#006B5A',
  contrastText: '#514538'
};
// Info uses brand teal
const INFO = {
  lighter: '#CCFAF5',
  light: '#66EDDA',
  main: '#00DABB',
  dark: '#00A086',
  darker: '#006B5A',
  contrastText: '#514538'
};
// Success — semantic green (distinct from teal)
const SUCCESS = {
  lighter: '#D7F7B0',
  light: '#89E219',
  main: '#58CC02',
  dark: '#46A302',
  darker: '#357A02',
  contrastText: '#fff'
};
// Warning — golden yellow (distinct from amber primary)
const WARNING = {
  lighter: '#FFF8D6',
  light: '#FFE566',
  main: '#FFD000',
  dark: '#CC9F00',
  darker: '#7A5F00',
  contrastText: '#514538'
};
// Error — semantic red, pinned to the UI kit's exact tokens (main/lighter/dark all
// literal kit values — dark is the kit's own "Again" rating shadow color)
const ERROR = {
  lighter: '#FBDCDD',
  light: '#F0888C',
  main: '#E5484D',
  dark: '#B23438',
  darker: '#7A2124',
  contrastText: '#fff'
};

const GRADIENTS = {
  primary: createGradient(PRIMARY.light, PRIMARY.main),
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main)
};

const CHART_COLORS = {
  amber: ['#FFA305', '#FFD166', '#FFE8B0', '#FFF3D6'],
  teal: ['#00DABB', '#66EDDA', '#ADFAE9', '#CCFAF5'],
  green: ['#58CC02', '#89E219', '#B8F47A', '#D7F7B0'],
  yellow: ['#FFD000', '#FFE566', '#FFF0AA', '#FFF8D6'],
  red: ['#FF4B4B', '#FF8585', '#FFBBBB', '#FFE0E0']
};

// User-assignable deck identity colors (deck card top-band, detail header swatch, color
// picker swatches). Keys must match backend/src/modules/decks/decks.constants.js's
// DECK_COLORS enum exactly — the backend only ever stores the key, this map resolves it
// to a hex value for display.
const DECK_COLORS = {
  blue: '#5B9BF0',
  teal: '#00DABB',
  green: '#58CC02',
  yellow: '#FFD000',
  orange: '#F2A54A',
  red: '#E5484D',
  purple: '#9B87F5'
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  // Pixel-art outline/shadow ink — stays pure black in both light and dark mode
  pixelInk: '#000000',
  primary: { ...PRIMARY },
  secondary: { ...SECONDARY },
  info: { ...INFO },
  success: { ...SUCCESS },
  warning: { ...WARNING },
  error: { ...ERROR },
  grey: GREY,
  gradients: GRADIENTS,
  divider: GREY[300],
  chart: CHART_COLORS,
  deck: DECK_COLORS,
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48
  }
};

const palette = {
  light: {
    ...COMMON,
    text: { primary: GREY[800], secondary: GREY[500], disabled: GREY[400] },
    // default = the UI kit's cream app-screen background (#fffbf4); paper = white
    // cards sitting on top of it, exactly matching the kit's screen/card layering.
    background: { paper: '#fff', default: '#FFFBF4', neutral: GREY[100] },
    action: { active: GREY[600], ...COMMON.action }
  },
  dark: {
    ...COMMON,
    text: { primary: '#FAF8F6', secondary: GREY[500], disabled: GREY[600] },
    background: { paper: GREY[800], default: GREY[900], neutral: GREY[500_16] },
    action: { active: GREY[500], ...COMMON.action }
  }
};

export default palette;
export { DECK_COLORS };
